import api from "./apiClient";
const API_URL = "https://utility.wiiz.it/api/admin/audit-logs/overview";
const EXPORT_API_URL = "https://utility.wiiz.it/api/admin/audit-logs/export";

const CACHE = new Map();
const CACHE_TTL = 1 * 60 * 1000; // 1 Minute

/**
 * Generate a unique cache key based on params
 */
const getCacheKey = (timePeriod, planFilter, customRange) => {
    return `AUDIT_${timePeriod}_${planFilter || 'ALL'}_${customRange ? JSON.stringify(customRange) : ''}`;
};



/**
 * Fetches audit log data from the API.
 * 
 * @param {string} timePeriod - Time period for log filtering.
 * @param {string|null} planFilter - Plan filter (education, basic, starter).
 * @param {boolean} forceRefresh - Whether to bypass cache.
 * @returns {Promise<Object>} Audit logs and associated KPIs.
 */
export async function fetchAuditLogs(timePeriod = "weekly", customRange = null, planFilter = null, forceRefresh = false) {
    const cacheKey = getCacheKey(timePeriod, planFilter, customRange);
    const cachedEntry = CACHE.get(cacheKey);
    const now = Date.now();

    if (!forceRefresh && cachedEntry && now - cachedEntry.timestamp < CACHE_TTL) {
        return cachedEntry.data;
    }

    const payload = {
        tenant_scope: null, // Default as per requirement
        plan_filter: planFilter,
        event_type_filter: "all",
        search_query: null,
        limit: 100
    };

    if (timePeriod === "custom") {
        if (customRange?.start && customRange?.end) {
            payload.start_date = customRange.start;
            payload.end_date = customRange.end;
        } else {
            console.warn("Skipping Audit API call: Custom range incomplete");
            return { data: [] }; // Return empty mock or valid structure
        }
    } else {
        payload.time_period = timePeriod;
    }

    console.log(`Fetching Audit Logs from: ${API_URL}`, payload);

    try {
        const response = await api.post(API_URL, payload);

        // Assuming API returns { status: "success", data: { ... } } or just { ... }
        // We might need to unwrap 'data' if it exists.
        // api response.data is the body
        const res = response.data;
        const data = res.data || res;

        CACHE.set(cacheKey, {
            data: data,
            timestamp: Date.now()
        });

        return data;
    } catch (error) {
        console.error("Audit Logs API Error:", error);
        throw error;
    }
}

/**
 * Exports audit logs as a CSV file.
 * 
 * @param {string} timePeriod - Time period for log filtering (daily, weekly, etc.).
 * @returns {Promise<Blob>} The CSV file blob.
 */
export async function exportAuditLogs(timePeriod, customRange = null) {
    const payload = {};
    if (timePeriod === "custom" && customRange) {
        payload.start_date = customRange.start;
        payload.end_date = customRange.end;
    } else {
        payload.time_period = timePeriod;
    }

    console.log(`Exporting Audit Logs from: ${EXPORT_API_URL}`, payload);

    try {
        const response = await api.post(EXPORT_API_URL, payload);
        return response.data;
    } catch (error) {
        console.error("Audit Logs Export Error:", error);
        throw error;
    }
}
